import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import { Table } from 'antd';
import dayjs from 'dayjs';
import { CustomSwitch } from 'components/pages/Threshold';
import { salesGraphWidth } from 'components/template/PriceChange/SalesChartContainerPriceChange';
import { colorConstants } from 'config/colorConstants';
import InactivationModal from '../Modals/InactivationModal';
import { priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import { getPortfolioUpdateMoreInfoColumns } from 'utils/getPriceSimulatorColumns';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { getIntersectionOnApplyFilter } from 'utils/helperMethods';
import { FilterContextData } from 'contexts/FilterContext';
import _ from 'lodash';
import { defaultApplyFilter } from 'components/pages/PriceChange';
import { useQuarterMetricData } from 'hooks/priceSimulator';
import EditPencilOutlined from 'assets/svg/EditPencilOutlined';
import DeleteOutlined from 'assets/svg/DeleteOutlined';
import CrossAlt from 'assets/svg/CrossAlt';
import ConfirmEventModal from 'components/organism/PromoRecommendation/ConfirmationModal';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PSR_PROTFOLIO_ACTIVATION_INACTIVATION_POPUP_CLICKED } from 'utils/mixpanelEventNames';

type MoreInfoTableProps = {
    onCloseIconClick: Function;
    onEditClick: Function;
};
const MoreInfoTable = ({ onCloseIconClick, onEditClick }: MoreInfoTableProps) => {
    const {
        portfolioUpdateKpisData,
        setPortfolioUpdateKpisData,
        expandedRowDetails,
        simulationTimePeriodSelection,
        selectedDataSource,
    } = useContext(priceSimulatorDataContext);
    const [showInactivePopUp, setShowInactivePopUp] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [isAddedCell, setIsAddedCell] = useState(false);
    const [inactiveDate, setInactiveDate] = useState<any>(null);
    const { idForRestOfComponent } = useContext(FilterContextData);
    const {
        data: applyFilterData,
        loading: applyFilterMutateDataLoading,
        error: applyFilterDataError,
        applyFilterMutate: applyFilterDataMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete });

    const {
        data: quarterMetricData,
        loading: quarterMetricLoading,
        error: quarterMetricError,
        fetchQuarterMetricData,
    } = useQuarterMetricData();

    function onApplyFilterComplete(payload: any) {
        const tempApplyFilterResponse = getIntersectionOnApplyFilter(idForRestOfComponent, payload);

        const quarterMetricVariables = {
            filters: JSON.stringify(tempApplyFilterResponse),
            timePeriod: String(simulationTimePeriodSelection).toLowerCase(),
            dataSourceType: selectedDataSource.value,
        };
        fetchQuarterMetricData({ variables: quarterMetricVariables });
    }

    useEffect(() => {
        if (expandedRowDetails.columnName) {
            const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
            const dim1Variables = {
                attributeExId: expandedRowDetails.rowName,
                attributeDesc: expandedRowDetails.rowName,
                attributeType: 'product',
                dimension: 'dim1',
            };
            const dim2Variables = {
                attributeExId: expandedRowDetails.columnName,
                attributeDesc: expandedRowDetails.columnName,
                attributeType: 'retailer',
                dimension: 'dim2',
            };

            tempApplyFilterVariable.jsonstring_dim1.inclusion = [dim1Variables];
            tempApplyFilterVariable.jsonstring_dim2.inclusion = [dim2Variables];
            tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
            tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
            applyFilterDataMutate({ variables: tempApplyFilterVariable });
        }
    }, [expandedRowDetails]);

    useEffect(() => {
        targetData.isActive = isActive;
        targetData.inActivationDate = [inactiveDate];
        const utcOffset = dayjs().utcOffset();
        const startDateUnix = dayjs(inactiveDate * 1000)
            .add(utcOffset, 'm')
            .subtract(6, 'day')
            .unix();
        targetData.inActivationDateEpochTime = [inactiveDate ? startDateUnix : null];
        setPortfolioUpdateKpisData([...portfolioUpdateKpisData]);
    }, [isActive, inactiveDate]);

    const setInactivateClick = (isImmediate: boolean, inactivationDate: any) => {
        setShowInactivePopUp(false);
        if (isImmediate) {
            setIsActive(false);
            setInactiveDate(null);
        } else {
            setIsActive(true);
            const startDateUnix = dayjs(inactivationDate[1]).unix();
            setInactiveDate(startDateUnix);
        }
    };

    const displayDate = useMemo(() => {
        if (inactiveDate) {
            return dayjs(inactiveDate * 1000).format('MM/DD/YYYY');
        }
        return '';
    }, [inactiveDate]);

    const targetData = useMemo(() => {
        let data: any = {};

        if (expandedRowDetails.columnName) {
            data = portfolioUpdateKpisData[expandedRowDetails.rowIndex - 1][expandedRowDetails.columnName];
            setIsActive(data?.isActive);
            setInactiveDate(data?.inActivationDate[0]);
            setIsAddedCell(data?.inputList?.length > 0);
        }

        return data;
    }, [expandedRowDetails]);

    const quarterMetricsColumns = useMemo(() => {
        if (quarterMetricData.length) {
            const columnNames = Object.keys(quarterMetricData[0]);
            return getPortfolioUpdateMoreInfoColumns(columnNames);
        }
        return [];
    }, [quarterMetricData]);

    const commonLoader = applyFilterMutateDataLoading || quarterMetricLoading;

    const [showDeletePopUp, setShowDeletePopUp] = useState(false);

    const onConfirmDeleteHandler = () => {
        setShowDeletePopUp(false);
        targetData.isActive = true;
        targetData.inActivationDate = [null];
        targetData.inActivationDateEpochTime = [null];
        targetData.value = null;
        targetData.inputList = {};
        setPortfolioUpdateKpisData([...portfolioUpdateKpisData]);
        onCloseIconClick();
    };

    const onDeleteClick = () => {
        // deleteNewlyAddedCell.current = key;
        setShowDeletePopUp(true);
    };

    return (
        <div className="rounded overflow-hidden">
            <div className="flex justify-between items-center my-4">
                <div className="flex ">
                    <span className="font-bold mr-8">{`${expandedRowDetails.rowName}/${expandedRowDetails.columnName}`}</span>
                    <div className="flex items-center justify-between">
                        <div className="flex">
                            <span className="mr-2">In-Active</span>
                            <CustomSwitch
                                checked={isActive}
                                onChange={(e) => {
                                    if (!e) {
                                        setShowInactivePopUp(true);
                                        trackEventMixpanel(PSR_PROTFOLIO_ACTIVATION_INACTIVATION_POPUP_CLICKED);
                                    } else {
                                        setIsActive(true);
                                    }
                                }}
                            />
                            <span className="ml-2 pr-5">Active</span>
                            {displayDate && (
                                <div className="flex py-1 px-2 bg-userProfile rounded font-xs">
                                    <span>
                                        {`${expandedRowDetails.rowName}/${expandedRowDetails.columnName}`} will get
                                        In-Active on : {displayDate}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex gap-3 items-center">
                    {isAddedCell && (
                        <>
                            <span className="cursor-pointer" onClick={() => onEditClick()}>
                                <EditPencilOutlined stroke={colorConstants.fontGrey} />
                            </span>
                            <span className="cursor-pointer" onClick={() => onDeleteClick()}>
                                <DeleteOutlined stroke={colorConstants.fontGrey} />
                            </span>
                        </>
                    )}
                    <span onClick={() => onCloseIconClick()} className="cursor-pointer">
                        <CrossAlt />
                    </span>
                </div>
            </div>

            {!isAddedCell && (
                <CustomTable
                    dataSource={quarterMetricData}
                    columns={quarterMetricsColumns as any}
                    bordered={false}
                    pagination={false}
                    scroll={{ x: '100%', y: 185 }}
                    sticky
                    loading={commonLoader}
                />
            )}
            <InactivationModal
                visible={showInactivePopUp}
                closeModal={setShowInactivePopUp}
                onSaveClick={setInactivateClick}
                targetData={targetData}
            />
            <ConfirmEventModal
                onYes={onConfirmDeleteHandler}
                setShowModal={setShowDeletePopUp}
                showModal={showDeletePopUp}
                confirmationTextFromParent={`This will delete the ${expandedRowDetails.rowName} from ${expandedRowDetails.columnName}. You can always add them back from Add PPG button or by directly clicking onto the cell.`}
            />
        </div>
    );
};

export default MoreInfoTable;

const CustomTable = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.borderGreyAlt};
        padding: 0px;
        height: 68px;
        color: ${colorConstants.borderGreyAlt};
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: ${salesGraphWidth}px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    border: ${'1px solid #DBDFE9'};
`;
